import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem("session_token");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Direct-Google flow on split-origin deployments (Pages + Railway): the
    // backend's OAuth callback relays the session token in the URL fragment
    // (#gtoken=...). Store it and scrub the address bar BEFORE the first
    // /me call, so history never keeps the token and refresh() authenticates.
    const hash = window.location.hash || "";
    const gtoken = hash.match(/[#&]gtoken=([^&]+)/)?.[1];
    if (gtoken) {
      try { localStorage.setItem("session_token", gtoken); } catch { /* ignore */ }
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    // If returning from Emergent OAuth, skip initial /me — AuthCallback will set us up.
    if (!gtoken && hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Phase 8: api.js dispatches this when any call 403s with account_suspended
  // (banned mid-session) — refreshing pulls banned:true and the App-level
  // gate swaps in the suspended view.
  useEffect(() => {
    const onSuspended = () => refresh();
    window.addEventListener("civic:suspended", onSuspended);
    return () => window.removeEventListener("civic:suspended", onSuspended);
  }, [refresh]);

  const logout = async () => {
    try { await api.logout(); } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
